// ANY EDITS TO THIS PAGE SHOULD ALSO BE MADE TO ITS DEMO COUNTERPART, TO KEEP THE APP AND DEMO IN SYNC
import './JoinPage.css';

import React, { useState } from 'react';

import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { usePostHog } from '@posthog/react';

import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PomPom from '../components/common/PomPom/PomPom';
import {
  JoinError,
  joinTryout,
} from '../services/joinService';

type JoinState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string };

type DebugPreview = 'none' | 'success' | 'error';

const JoinPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const logo = `${process.env.PUBLIC_URL}/logo.svg`;

  const [code, setCode] = useState(() =>
    (searchParams.get('code') ?? '').toUpperCase()
  );
  const [password, setPassword] = useState('');
  const [coachName, setCoachName] = useState('');
  const [joinState, setJoinState] = useState<JoinState>({ status: 'idle' });
  const [debugPreview, setDebugPreview] = useState<DebugPreview>('none');
  const [appCheckFailureCount, setAppCheckFailureCount] = useState(0);

  const isLoading = joinState.status === 'loading';

  const displayError =
    debugPreview === 'error'
      ? 'Invalid session code'
      : joinState.status === 'error'
        ? joinState.message
        : '';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const trimmedCode = code.trim();
    const trimmedPassword = password.trim();
    const trimmedName = coachName.trim();
    if (!trimmedCode || !trimmedPassword || !trimmedName) {
      setJoinState({ status: 'error', message: 'Please fill in all fields.' });
      return;
    }
    setJoinState({ status: 'loading' });
    try {
      const forceRefresh = appCheckFailureCount > 0;
      const result = await joinTryout(trimmedCode, trimmedName, trimmedPassword, forceRefresh);
      setAppCheckFailureCount(0);
      posthog?.identify(trimmedName);
      posthog?.capture('tryout_joined', { tryout_id: result.tryoutId, coach_name: trimmedName });
      // Navigate immediately; success screen is debug-only.
      navigate(`/tryout/${result.tryoutId}`);
    } catch (err) {
      const message =
        err instanceof JoinError
          ? err.message
          : 'Failed to join. Please check your connection and try again.';
      if (message.includes('App verification')) {
        setAppCheckFailureCount(c => c + 1);
      }
      posthog?.captureException(err);
      setJoinState({ status: 'error', message });
    }
  };

  const handleDebugSuccess = (): void => setDebugPreview((p) => (p === 'success' ? 'none' : 'success'));
  const handleDebugError   = (): void => setDebugPreview((p) => (p === 'error'   ? 'none' : 'error'));

  const debugControls = false ? (
    <div className="join-page-debug">
      <Button type="button" variant="secondary" compact onClick={handleDebugSuccess}>
        {debugPreview === 'success' ? 'Hide' : 'Show'} success (debug)
      </Button>
      <Button type="button" variant="secondary" compact onClick={handleDebugError}>
        {debugPreview === 'error' ? 'Hide' : 'Show'} error (debug)
      </Button>
    </div>
  ) : null;

  // Debug-only success preview (real success navigates away immediately).
  if (debugPreview === 'success') {
    const displayName = coachName.trim() || 'Coach';
    return (
      <div className="join-page">
        <div className="join-gutter-pompoms" aria-hidden="true">
          <PomPom size="xlarge" color="pink" className="join-gutter-pompom join-gutter-pompom-right-top" />
          <PomPom size="large" color="teal" className="join-gutter-pompom join-gutter-pompom-left-mid" />
          <PomPom size="xlarge" color="teal" className="join-gutter-pompom join-gutter-pompom-right-bottom" />
          <PomPom size="medium" color="pink" className="join-gutter-pompom join-gutter-pompom-left-bottom" />
          <PomPom size="large" color="pink" className="join-gutter-pompom join-gutter-pompom-right-edge-top" />
          <PomPom size="large" color="teal" className="join-gutter-pompom join-gutter-pompom-left-edge-top" />
          <PomPom size="xlarge" color="teal" className="join-gutter-pompom join-gutter-pompom-right-edge-bottom" />
          <PomPom size="medium" color="pink" className="join-gutter-pompom join-gutter-pompom-left-edge-bottom" />
        </div>
        <div className="join-hero">
          <PomPom size="medium" color="pink" className="join-hero-pompom join-hero-pompom-tr" />
          <PomPom size="small"  color="teal" className="join-hero-pompom join-hero-pompom-bl" />
          <img src={logo} alt="CheerEval" className="join-logo" />
        </div>
        <div className="join-card">
          <div className="join-success-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#fff" />
            </svg>
          </div>
          <h2 className="join-success-title">You're in!</h2>
          <p className="join-success-name">Welcome, {displayName}.</p>
          <p className="join-success-msg">You've joined the tryout successfully.</p>
        </div>
        {debugControls}
      </div>
    );
  }

  return (
    <div className="join-page">
      <div className="join-gutter-pompoms" aria-hidden="true">
        <PomPom size="xlarge" color="pink" className="join-gutter-pompom join-gutter-pompom-right-top" />
        <PomPom size="large" color="teal" className="join-gutter-pompom join-gutter-pompom-left-mid" />
        <PomPom size="xlarge" color="teal" className="join-gutter-pompom join-gutter-pompom-right-bottom" />
        <PomPom size="medium" color="pink" className="join-gutter-pompom join-gutter-pompom-left-bottom" />
        <PomPom size="large" color="pink" className="join-gutter-pompom join-gutter-pompom-right-edge-top" />
        <PomPom size="large" color="teal" className="join-gutter-pompom join-gutter-pompom-left-edge-top" />
        <PomPom size="xlarge" color="teal" className="join-gutter-pompom join-gutter-pompom-right-edge-bottom" />
        <PomPom size="medium" color="pink" className="join-gutter-pompom join-gutter-pompom-left-edge-bottom" />
      </div>
      {/* Maroon hero with logo + heading */}
      <div className="join-hero">
        <PomPom size="medium" color="pink" className="join-hero-pompom join-hero-pompom-tr" />
        <PomPom size="small"  color="teal" className="join-hero-pompom join-hero-pompom-bl" />
        <div className="join-hero-header-row">
          <h1 className="join-hero-title">
            Join<br />
            <span className="join-hero-accent">tryout</span>
          </h1>
          <img src={logo} alt="CheerEval" className="join-logo" />
        </div>
        <p className="join-hero-sub">Enter the code your coach shared to get started.</p>
      </div>

      {/* Form card */}
      <div className="join-card">
        {displayError ? (
          <div className="join-error" role="alert">{displayError}</div>
        ) : null}

        <form className="join-form" onSubmit={(e) => { void handleSubmit(e); }}>
          <Input
            type="text"
            name="cheereval-tryout-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            label="Tryout Code"
            autoComplete="cheereval-tryout-code"
            placeholder="Enter 6-character code"
            required
            autoCapitalize="characters"
            disabled={isLoading}
          />
          <Input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label="Tryout Password"
            placeholder="Enter tryout password"
            required
            autoComplete="off"
            autoCapitalize="off"
            disabled={isLoading}
          />
          <Input
            type="text"
            value={coachName}
            onChange={(e) => setCoachName(e.target.value)}
            label="Your Name"
            placeholder="Enter your name"
            required
            disabled={isLoading}
          />
          <button type="submit" className="join-btn-submit" disabled={isLoading}>
            {isLoading ? 'Joining…' : 'Join Tryout'}
          </button>
        </form>

        <button type="button" className="join-link-back" onClick={() => navigate('/')}>
          ← Back to home
        </button>
      </div>

      {debugControls}
    </div>
  );
};

export default JoinPage;
